<template>
  <SettingsPagePanel v-if="isAvailable">
    <SettingsPagePanelHeader>
      <template #title>アプローチ自動代行</template>
      <template #right>
        <RouterLink
          :to="{
            name: 'attackListSenderSettingNew',
          }"
        >
          <FlatButton>新しい条件を作成</FlatButton>
        </RouterLink>
      </template>
    </SettingsPagePanelHeader>

    <div :class="$style.control">
      <section :class="$style.checkboxSection">
        <label :class="$style.checkbox">
          <DolphinCheckbox
            :modelValue="bulkChecked"
            @update:modelValue="clickBulkChecked"
          />
          一括でチェック
        </label>

        <FlatButton
          @click="handleDelete"
          :disabled="settingsWithCheck.every(value => !value.checked)"
          variant="caution"
          >削除</FlatButton
        >
      </section>

      <p>現在の設定数: {{ settings.length }}</p>
    </div>

    <ul :class="$style.list">
      <li
        v-for="(setting, index) in settingsWithCheck"
        :key="setting.id"
        :class="$style['listItem']"
      >
        <SettingsPageAttackListSenderViewSettingItem
          :setting="setting"
          :checked="setting.checked"
          @changeCheck="checked => handleChangeChecked(index, checked)"
        />
      </li>
    </ul>
  </SettingsPagePanel>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { captureException as newrelicCaptureException } from '@/newrelic'
import { useRouter } from 'vue-router'

import FlatButton from '@/components/atoms/FlatButton.vue'
import DolphinCheckbox from '@/components/atoms/DolphinCheckbox.vue'

import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPageAttackListSenderViewSettingItem from './SettingsPageAttackListSenderViewSettingItem.vue'

import {
  useFetchSettingsOnSettingsPageAttackListSenderViewQuery,
  useDeleteAttackListSenderSettingsMutation,
  type FetchSettingsOnSettingsPageAttackListSenderViewQuery,
  FetchSettingsOnSettingsPageAttackListSenderViewDocument,
} from './SettingsPageAttackListSenderView.generated'
import { useAlert } from '@/libs/alertPortal'
import { useApolloClient } from '@vue/apollo-composable'
import { useLogger } from '@/logger/logger'

const { result, refetch } =
  useFetchSettingsOnSettingsPageAttackListSenderViewQuery({
    fetchPolicy: 'no-cache',
  })

const apollo = useApolloClient()

const { data } =
  await apollo.client.query<FetchSettingsOnSettingsPageAttackListSenderViewQuery>(
    {
      query: FetchSettingsOnSettingsPageAttackListSenderViewDocument,
    }
  )

const isAvailable = data.viewer.attackListSender.available

const router = useRouter()

if (!isAvailable) {
  // eslint-disable-next-line fp/no-mutating-methods
  router.push({ name: 'errorNotFound' })
}

const settings = computed(
  () => result.value?.viewer.attackListSender.settings ?? []
)

const settingsWithCheck = computed(() =>
  settings.value.map(setting => ({
    ...setting,
    checked: checkedSettingIds.value.includes(setting.id),
  }))
)

// この値を使って、一括で削除するようにする
const checkedSettingIds = ref<string[]>([])

const handleChangeChecked = (index: number, checked: boolean) => {
  if (checked) {
    checkedSettingIds.value = Array.from(
      new Set([...checkedSettingIds.value, settings.value[index].id])
    )
    return
  }
  checkedSettingIds.value = checkedSettingIds.value.filter(
    id => id !== settings.value[index].id
  )
}

const bulkChecked = computed(() => {
  // 初回読み込み時と削除後に「一括でチェック」がオンになるのを防ぐ
  if (checkedSettingIds.value.length === 0) {
    return false
  }

  return checkedSettingIds.value.length === settings.value.length
})

const clickBulkChecked = () => {
  if (!bulkChecked.value) {
    checkedSettingIds.value = settings.value.map(setting => setting.id)
    return
  }
  checkedSettingIds.value = []
}

const alert = useAlert()
const logger = useLogger()

const { mutate } = useDeleteAttackListSenderSettingsMutation({})

const handleDelete = async () => {
  if (!window.confirm('選択した設定を削除しますか？')) {
    return
  }
  try {
    const deletedSettings = settings.value
      .filter(setting => checkedSettingIds.value.includes(setting.id))
      .map(setting => ({
        id: Number(setting.id),
        plannerId: Number(setting.planner.id),
        entranceGuid: setting.entrance.guid,
        isEnabled: setting.isEnabled,
        rulesJson: JSON.stringify(setting.rule),
        title: setting.title,
        body: setting.body,
        selections: setting.selections,
      }))
    await mutate({
      ids: checkedSettingIds.value,
    })
    logger.ltlPeople.event.deleteAttackListSenderSettings(logger.buildMeta(), {
      data: deletedSettings,
    })
  } catch (e) {
    alert.error('削除に失敗しました、もう一度お試しください')
    console.error(e)

    newrelicCaptureException(e)
    return
  }
  refetch()
  alert.success('削除しました')
}
</script>

<style lang="scss" module>
.control {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  justify-content: space-between;
}

.checkboxSection {
  display: flex;
  align-items: center;
  gap: 32px;
}

.checkbox {
  display: flex;
  align-items: center;
  gap: 12px;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &:nth-child(n + 2) {
    margin-top: 12px;
  }
}
</style>
